'use client';

import type { FeatureStepsProps } from '@/core/types';

import React from 'react';
import { CONFIG } from '@/global-config';

import { Box, Image, Container, Typography } from '@/components/ui';

export default function FeatureStepsV1({ steps, className }: FeatureStepsProps) {
  return (
    <Box className={`${className} relative w-full bg-light-gray/56  py-16`}>
      <Container>
        <Box className="relative w-full bg-white shadow-03  overflow-hidden pt-26 pb-26 xl:pb-42 pl-9 pr-9 xl:pl-20 2xl:pl-24 xl:pr-52 rounded-lg -mt-[330px] z-10">
          <Image
            src={`${CONFIG.assetsDir}/assets/images/home/feature-steps.webp`}
            alt="bg"
            objectFit="cover"
            className="w-full absolute top-0 left-0 h-full object-cover -z-[1]"
            loading="lazy"
            width={1920}
            height={1080}
          />

          <Box className="relative flex flex-col lg:flex-row lg:justify-between  2xl:justify-normal lg:gap-4 xl:gap-8">
            <Box className="absolute top-1/2  left-0 right-0 h-0 bg-gray-200 z-0" />

            {steps?.map((step, index) => {
              const isEven = index % 2 != 0;
              return (
                <Box
                  key={step.id}
                  className={`relative flex-col w-full flex gap-3 items-center xl:flex-row z-10 ${isEven ? 'lg:mb-[138px] lg:flex-col-reverse xl:items-start' : 'lg:mt-[138px] lg:flex-col xl:items-end'}`}
                >
                  {/* SVG-маркер */}
                  <Box
                    className={`h-[96px] lg:h-full flex w-full lg:w-fit xl:-mr-[34px]  ${!isEven ? 'lg:rotate-180' : ''}`}
                  >
                    <StepMarker color={isEven ? 'purple' : 'green'} />
                  </Box>

                  <Box className="relative">
                    <Box
                      className={`text-center xl:text-left left-2  ${!isEven ? 'xl:-top-[42px]' : 'xl:-top-[30px]'}  xl:absolute xl:w-[270px]`}
                    >
                      <Typography
                        variant="h3"
                        className="text-base uppercase text-black text-center xl:text-left"
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        paragraph
                        className={`text-base font-bold text-center xl:text-left ${isEven ? 'text-secondary' : 'text-primary'} `}
                      >
                        {step.highlight}
                      </Typography>
                      <Typography
                        variant="body1"
                        paragraph
                        className="text-sm text-center xl:text-left text-gray-600 mt-1"
                      >
                        {step.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

type StepMarkerProps = {
  color: 'green' | 'purple';
};

function StepMarker({ color }: StepMarkerProps) {
  const fillColor = color === 'green' ? '#3bd181' : '#B216D4'; // Green or Purple

  return (
    <svg
      className="w-full rotate-90 lg:rotate-0 lg:w-[91px] lg:h-[208px]"
      viewBox="0 0 91 208"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M39.3681 207.473C17.6781 207.473 0.0380859 189.833 0.0380859 168.143C0.0380859 146.453 17.6781 128.813 39.3681 128.813C61.0581 128.813 78.6981 146.453 78.6981 168.143C78.6981 189.833 61.0581 207.473 39.3681 207.473ZM39.3681 129.803C18.2281 129.803 1.03809 147.003 1.03809 168.133C1.03809 189.263 18.2381 206.463 39.3681 206.463C60.4981 206.463 77.6981 189.263 77.6981 168.133C77.6981 147.003 60.4981 129.803 39.3681 129.803Z"
        fill="#6D6E71"
      />
      <path
        d="M39.3683 192.233C26.0783 192.233 15.2783 181.423 15.2783 168.143C15.2783 154.863 26.0883 144.053 39.3683 144.053C52.6483 144.053 63.4583 154.863 63.4583 168.143C63.4583 181.423 52.6483 192.233 39.3683 192.233ZM39.3683 147.043C27.7383 147.043 18.2783 156.503 18.2783 168.133C18.2783 179.763 27.7383 189.223 39.3683 189.223C50.9983 189.223 60.4583 179.763 60.4583 168.133C60.4583 156.503 50.9983 147.043 39.3683 147.043Z"
        fill={fillColor}
      />
      <path
        d="M28.0684 168.133C28.0684 161.893 33.1283 156.833 39.3683 156.833C45.6083 156.833 50.6683 161.893 50.6683 168.133C50.6683 174.373 45.6083 179.433 39.3683 179.433C33.1283 179.433 28.0684 174.373 28.0684 168.133Z"
        fill={fillColor}
      />
      <path
        d="M32.8379 6.63303C32.8379 3.02303 35.7579 0.103027 39.3679 0.103027C42.9779 0.103027 45.8979 3.02303 45.8979 6.63303C45.8979 10.243 42.9779 13.163 39.3679 13.163C35.7579 13.163 32.8379 10.243 32.8379 6.63303Z"
        fill={fillColor}
      />
      <path d="M39.8682 4.51294H38.8682V129.303H39.8682V4.51294Z" fill={fillColor} />
      <path
        d="M90.4882 168.133H89.4882C89.4882 140.383 66.9082 117.803 39.1582 117.803V116.803C67.4682 116.803 90.4882 139.833 90.4882 168.133Z"
        fill="#6D6E71"
      />
    </svg>
  );
}
